import { ColDef, RowNode, ValueSetterParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import moment from 'moment';
import {
  ActionColumnActions,
  carrierValueGetter,
  gridDateFormatter,
  messageDateFormatter,
  nextStepsValueFormatter,
  physicianNameValueFormatter,
  providerNameValueFormatter,
  stringToNumberValueGetter,
  trackingValueGetter,
  patientHubStatusValueFormatter,
  dobDateFormatter,
  amountValueFormatter,
  requestStatusValueFormatter
} from '.';
import { AnyType } from '../../interfaces';

export const globalDefaultColDefs: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  autoHeight: true
};

const sortIconStyle = {
  sortAscending: '<span class="ag-icon ag-icon-small-up" style="color: #ADB5BD;"></span>',
  sortDescending: '<span class="ag-icon ag-icon-small-down" style="color: #ADB5BD;"></span>',
  sortUnSort: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <span class="ag-icon ag-icon-small-up" style="color: #ADB5BD; margin-bottom: -6px"></span>
        <span class="ag-icon ag-icon-small-down" style="color: #ADB5BD;"></span>
      </div>
    `,
  filterIcon: `
  <div data-svg-wrapper style={{position: 'relative'}}>
  <svg width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="20" height="17" transform="translate(0.5 0.586182)" fill="white" fill-opacity="0.01"/>
  <path d="M13.3067 11.5395C14.0329 10.5485 14.3582 9.31988 14.2174 8.09937C14.0767 6.87886 13.4802 5.75651 12.5475 4.95686C11.6148 4.1572 10.4145 3.73921 9.18681 3.78652C7.95913 3.83382 6.79457 4.34293 5.92614 5.21199C5.0577 6.08105 4.54942 7.24597 4.503 8.47368C4.45657 9.7014 4.87542 10.9014 5.67574 11.8335C6.47606 12.7657 7.59884 13.3613 8.81945 13.5012C10.0401 13.6411 11.2685 13.3149 12.2589 12.588H12.2582C12.2807 12.618 12.3047 12.6465 12.3317 12.6743L15.2192 15.5618C15.3598 15.7025 15.5506 15.7816 15.7495 15.7817C15.9485 15.7817 16.1393 15.7028 16.2801 15.5621C16.4208 15.4215 16.4999 15.2307 16.5 15.0318C16.5 14.8328 16.4211 14.642 16.2804 14.5013L13.3929 11.6138C13.3661 11.5866 13.3373 11.5615 13.3067 11.5388V11.5395ZM13.5002 8.65651C13.5002 9.19821 13.3935 9.73461 13.1862 10.2351C12.9789 10.7355 12.675 11.1903 12.292 11.5733C11.909 11.9564 11.4542 12.2602 10.9538 12.4675C10.4533 12.6748 9.91689 12.7815 9.37519 12.7815C8.83348 12.7815 8.29708 12.6748 7.79662 12.4675C7.29615 12.2602 6.84141 11.9564 6.45837 11.5733C6.07533 11.1903 5.77148 10.7355 5.56418 10.2351C5.35688 9.73461 5.25019 9.19821 5.25019 8.65651C5.25019 7.56249 5.68478 6.51328 6.45837 5.73969C7.23196 4.96611 8.28117 4.53151 9.37519 4.53151C10.4692 4.53151 11.5184 4.96611 12.292 5.73969C13.0656 6.51328 13.5002 7.56249 13.5002 8.65651Z" fill="#ADB5BD"/>
  </svg>
  </div>
    `
};

// -----===[ColDefs]===-----

export const actionsColumnDefinition = (actions: (node: RowNode) => ActionColumnActions[]): ColDef => ({
  cellEditor: 'ActionsCellEditor',
  cellClass: 'action-cell-options',
  cellEditorParams: {
    cellRenderer: 'ActionsCellRenderer',
    values: actions
  },
  cellRenderer: 'ActionsCellRenderer',
  editable: true,
  filter: false,
  maxWidth: 85,
  resizable: false,
  singleClickEdit: true,
  sortable: false,
  suppressSizeToFit: true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  valueSetter: (params: ValueSetterParams) => false
});

export const alertColumnDefinition = (cellRenderer = 'AlertCellRenderer'): ColDef => ({
  cellRenderer: cellRenderer,
  filter: false,
  headerName: '',
  maxWidth: 75,
  resizable: false,
  sortable: false,
  suppressSizeToFit: true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  valueSetter: (params: ValueSetterParams) => false
});

export const nameColumnDefinition = ({ headerName, hide }: AnyType = {}) => ({
  headerName,
  hide,
  comparator: customComparator
});
export const customComparator = (valueA: string, valueB: string) => {
  return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
};

export const messageDirectionColumnDefinition = (headerName = ''): ColDef => ({
  headerName,
  cellRenderer: 'MessageCellRenderer',
  suppressAutoSize: true,
  suppressSizeToFit: true,
  maxWidth: 40,
  filter: false
});

export const messageColumnDefinition = (headerName = 'MESSAGE DETAIL'): ColDef => ({
  headerName,
  field: 'messageText',
  wrapText: true,
  autoHeight: true,
  suppressAutoSize: true,
  suppressSizeToFit: true,
  tooltipField: 'messageText',
  unSortIcon: true,
  filter: true,
  icons: {
    sortAscending: sortIconStyle.sortAscending,
    sortDescending: sortIconStyle.sortDescending,
    sortUnSort: sortIconStyle.sortUnSort,
    menu: sortIconStyle.filterIcon
  }
});

export const nextStepColumnWithoutTooltip = (headerName = 'Next Steps'): ColDef => ({
  headerName,
  cellRenderer: 'NextStepCellRenderer',
  field: 'nextStep',
  filterValueGetter: nextStepsValueFormatter,
  valueFormatter: nextStepsValueFormatter,
  valueGetter: nextStepsValueFormatter
});

export const nextStepColumnDefinition = (headerName = 'NEXT STEPS'): ColDef => ({
  headerName,
  cellRenderer: 'NextStepCellRenderer',
  field: 'nextStep',
  filterValueGetter: nextStepsValueFormatter,
  tooltipField: 'nextStep',
  tooltipValueGetter: (params) => nextStepsValueFormatter(params.value),
  valueFormatter: nextStepsValueFormatter,
  valueGetter: nextStepsValueFormatter
});

export const prescriberColumnDefinition = (headerName = 'Prescriber'): ColDef => ({
  field: 'physicianFirstName',
  filterValueGetter: physicianNameValueFormatter,
  headerName: headerName,
  valueFormatter: physicianNameValueFormatter,
  valueGetter: physicianNameValueFormatter
  //width: 215
});

export const patientHubStatusColumnDefinition = (
  field: string,
  headerName?: string,
  patientfiltersMapping?: AnyType
): ColDef => ({
  headerName,
  field,
  valueGetter: function (params: ValueFormatterParams | ValueGetterParams, filter?: typeof patientfiltersMapping) {
    return patientHubStatusValueFormatter(params, patientfiltersMapping);
  },
  width: 250
});

export const requestStatusColumnDefinition = (
  field: string,
  headerName?: string,
  requestfiltersMapping?: AnyType
): ColDef => ({
  headerName,
  field,
  valueGetter: function (params: ValueFormatterParams | ValueGetterParams) {
    // Get the Filtered status value
    const filteredStatusValue = requestStatusValueFormatter(params, requestfiltersMapping);
    if (Object.isExtensible(params.data)) {
      // Store the filteredStatusValue in a new field
      params.data.passingStatusValue = filteredStatusValue;
    }
    return filteredStatusValue;
  },
  width: 250
});

export const carrierColumnDefinition = (headerName = 'CARRIER'): ColDef => ({
  field: 'shippingCarrier',
  filterValueGetter: carrierValueGetter,
  headerName: headerName,
  valueFormatter: carrierValueGetter,
  valueGetter: carrierValueGetter
});
export const trackingColumnDefinition = (headerName = 'TRACKING'): ColDef => ({
  field: 'trackingNumber',
  filterValueGetter: trackingValueGetter,
  headerName: headerName,
  valueFormatter: trackingValueGetter,
  valueGetter: trackingValueGetter
});

export const providerColumnDefinition = (headerName = 'provider'): ColDef => ({
  field: 'providerFirstName',
  filterValueGetter: providerNameValueFormatter,
  headerName: headerName,
  valueFormatter: providerNameValueFormatter,
  valueGetter: providerNameValueFormatter
});
export const serviceRequestAlertColumnDefinition = (): ColDef =>
  alertColumnDefinition('ServiceRequestAlertCellRenderer');

export const caseAlertColumnDefinition = (): ColDef => alertColumnDefinition('CaseAlertCellRenderer');

export const viewActionColumnDefinition = (headerName = ''): ColDef => ({
  headerName,
  cellRenderer: 'ViewDocumentRenderer',
  filter: false,
  maxWidth: 85,
  resizable: false,
  sortable: false,
  suppressSizeToFit: true
});

export const dobDateColumnFormat = (field: string, headerName?: string): ColDef => ({
  headerName,
  field: 'date',
  filter: true,
  comparator: dateComparator,
  tooltipValueGetter: (params) => params.value,
  valueGetter: dobDateFormatter({ key: field }),
  filterParams: {
    ...dateFilterParams
  }
});

export const dateColumnDefinition = (field: string, headerName?: string): ColDef => ({
  headerName,
  field: 'date',
  filter: 'agDateColumnFilter',
  comparator: dateComparator,
  tooltipValueGetter: (params) => params.value,
  valueGetter: gridDateFormatter({ key: field }),
  filterParams: {
    ...dateFilterParams
  }
});

export const messageDateColumnDefinition = (field: string, headerName?: string): ColDef => ({
  headerName,
  field,
  tooltipValueGetter: (params) => params.value,
  valueGetter: messageDateFormatter({ key: field }),
  filterParams: {
    ...dateFilterParams
  },
  unSortIcon: true,
  filter: true,
  icons: {
    sortAscending: sortIconStyle.sortAscending,
    sortDescending: sortIconStyle.sortDescending,
    sortUnSort: sortIconStyle.sortUnSort,
    menu: sortIconStyle.filterIcon
  }
});

export const generalColumnDefinition = (): ColDef => ({
  unSortIcon: true,
  filter: true,
  icons: {
    sortAscending: sortIconStyle.sortAscending,
    sortDescending: sortIconStyle.sortDescending,
    sortUnSort: sortIconStyle.sortUnSort,
    menu: sortIconStyle.filterIcon
  }
});

export const drugDescription = (field: string, headerName?: string): ColDef => ({
  headerName,
  field,
  tooltipField: field,
  cellClass: 'ellipse-renderer'
});

export const idColumnDefinition = (field: string, headerName?: string): ColDef => ({
  headerName,
  field,
  filter: true,
  tooltipField: field,
  valueGetter: stringToNumberValueGetter
});

export const amountColumnDefinition = (field: string): ColDef => ({
  field,
  tooltipValueGetter: (params) => params.value,
  valueGetter: amountValueFormatter
});

export const dateColumnDefinitionWithTime = (field: string, headerName?: string): ColDef => ({
  headerName,
  field,
  filter: 'agDateColumnFilter',
  tooltipValueGetter: (params) => params.value,
  valueGetter: gridDateFormatter({ key: field, format: 'MM/DD/YYYY hh:mm A' }),
  filterParams: {
    ...dateFilterParams
  }
});
// View and Edit for  RxAdmin modal screen
export const rxAdminActionColumnDefinition = (headerName = 'ACTIONS'): ColDef => ({
  headerName,
  cellRenderer: 'RxAdminActionRenderer',
  filter: false,
  maxWidth: 85,
  resizable: false,
  sortable: false,
  suppressSizeToFit: true
});

export const rxAdminColumnDefinition = (field: string, headerName?: string): ColDef => ({
  headerName,
  field,
  filter: 'agDateColumnFilter',
  tooltipValueGetter: (params) => params?.data?.rxAdminStatus + '\n(Dose: ' + params?.data?.rxAdminDose + ')',
  valueGetter: gridDateFormatter({ key: field }),
  filterParams: {
    ...dateFilterParams
  }
});

export const rxAdminColumnWithTooltip = (cellRenderer = 'RxAdminHover'): ColDef => ({
  cellRenderer: cellRenderer
});

// -----===[Filter Params]===-----

export const dateFilterParams = {
  comparator(filterLocaleDate: Date, cellValue: string) {
    if (cellValue === '') return -1;

    const cellDate = moment(cellValue).utc();
    const filterDate = moment(filterLocaleDate).utc();

    return cellDate.isBefore(filterDate) ? -1 : cellDate.isAfter(filterDate) ? 1 : 0;
  }
};

// DATE COMPARATOR FOR SORTING
function dateComparator(date1: Date, date2: Date) {
  const date1Number = date1 && new Date(date1).getTime();
  const date2Number = date2 && new Date(date2).getTime();
  if (date1Number === null && date2Number === null) {
    return 0;
  }
  if (date1Number === null) {
    return -1;
  } else if (date2Number === null) {
    return 1;
  }
  return date1Number - date2Number;
}
